import { STRUCTURES, type HeroClass, type StructureType } from '@somnhold/content';
import { createBattle, createLandMap, generateWave, openFissures, type BattleState, type MapDef, type StructureInit } from '@somnhold/sim';

/**
 * The bastion from scratch, played locally (docs/CONSTRUCCION.md §10, stage 1): terraced land with the keep
 * alone on the crest, instant building with a generous purse, walls drawn by dragging and gates anywhere,
 * and the Tides I to III one after another. Each Tide cracks the field open in fissures nobody can foresee
 * and the nightmares climb out of them. Nothing here reaches a server or a campaign.
 */

export const LAND_BUILDS: StructureType[] = ['wall', 'gate', 'ballista', 'catapult', 'arcane', 'spikes', 'oil'];

/** The Tides it plays, one after another. */
export const LAND_MAREAS = 3;

export interface LandPoc {
  seed: number;
  cls: HeroClass;
  /** The Tide coming next (0 = I). */
  marea: number;
  map: MapDef;
  /** Tides played, retries included. */
  attempts: number;
  structures: StructureInit[];
  purse: { wood: number; stone: number; crystal: number; dust: number };
}

export const createLandPoc = (seed: number, cls: HeroClass): LandPoc => ({
  seed,
  cls,
  marea: 0,
  map: createLandMap(seed),
  attempts: 0,
  structures: [],
  purse: { wood: 1400, stone: 900, crystal: 240, dust: 0 },
});

export const landBattle = (p: LandPoc, name: string): BattleState => {
  const sectors = p.map.sectors.length;
  const wave = generateWave({
    seed: p.seed * 7 + p.marea * 101 + p.attempts * 13,
    marea: p.marea,
    wave: 3,
    sectors,
    activityBp: 20000,
    depth: 'light',
    lossPenalty: false,
    hosts: true,
  });
  p.attempts++;
  return createBattle({
    mode: 'practice',
    seed: p.seed + p.marea + p.attempts,
    // Each Tide (and each try) cracks the field open somewhere else.
    map: openFissures(p.map, p.structures, p.structures.filter((x) => STRUCTURES[x.type].blocks), p.seed * 31 + p.attempts * 977),
    structures: p.structures,
    heroes: [{ pid: 'me', name, cls: p.cls, level: 3 + p.marea * 2, control: 'live', powerBp: 10000, post: 0 }],
    keepHp: 4000,
    keepMaxHp: 4000,
    marea: p.marea,
    wave: 3,
    spawns: wave.spawns,
    purse: { ...p.purse },
    // The general cracks more fissures open mid-wave where the hero isn't, as in a campaign's wave.
    rules: { rifts: true },
  });
};

/** After a Tide: what still stands stays, and the purse carries over with the Tide's spoils. */
export const afterLandTide = (p: LandPoc, s: BattleState, next: boolean): void => {
  p.structures = s.structures.filter((st) => !st.destroyed).map((st) => ({ type: st.type, level: st.level, material: st.material, q: st.q, r: st.r }));
  const purse = s.purse ?? p.purse;
  p.purse = { wood: purse.wood + 600, stone: purse.stone + 400, crystal: purse.crystal + 90, dust: 0 };
  if (next) p.marea = Math.min(LAND_MAREAS - 1, p.marea + 1);
};
